/**
 * Pure data/geometry helpers for the chart, extracted from Chart.tsx:
 * candle → series-data mappers, whitespace generation, price-range
 * application, and time↔logical-index interpolation. No React, no component
 * state — just transforms over candle arrays + the chart/series handles.
 */
import type { IChartApi, ISeriesApi, UTCTimestamp } from "lightweight-charts";
import type { Candle } from "@/api/types";

export type AnySeries =
  | ISeriesApi<"Candlestick">
  | ISeriesApi<"Line">
  | ISeriesApi<"Area">;

// Trigger a prepend when the leftmost visible candle is within this many
// bars of the start. 200 keeps the chain to a single pre-fetch on initial
// paint (one month of buffer ready for a leftward pan) without compressing
// the visible view.
export const LAZY_LOAD_THRESHOLD = 200;

// Number of empty time-scale slots to append after the last real bar — gives
// the chart a draggable "future" so fib anchors can be placed past the most
// recent candle.
export const RIGHT_PADDING_BARS = 500;

// On fresh load, show at most this many of the most-recent real candles plus
// a 20% right-padding (so HTF datasets of thousands of bars don't collapse).
export const INITIAL_VISIBLE_BARS = 250;

// Max fraction of a *restored* visible window allowed to sit past the last real
// bar (in the trailing whitespace). A timeframe-change window is rebuilt centred
// on the old window's midpoint, and a saved viewport can have a `to` at the live
// edge — either can push the window's right side into empty future space,
// leaving candles bunched on the left. The restore slides the window left when
// `to` overshoots this fraction so candles keep filling the view. ~0.15 matches
// the ~17% right-pad fitContent leaves at the live edge.
export const RESTORE_FUTURE_PAD_FRAC = 0.15;

export function toLineData(candles: Candle[]) {
  return candles.map((c) => ({
    time: c.time as UTCTimestamp,
    value: c.close,
  }));
}

export function toCandleData(candles: Candle[]) {
  return candles.map((c) => ({
    time: c.time as UTCTimestamp,
    open: c.open,
    high: c.high,
    low: c.low,
    close: c.close,
  }));
}

export function generateWhitespace(
  lastTime: number,
  count: number,
  tfSeconds: number,
): { time: UTCTimestamp }[] {
  return Array.from({ length: count }, (_, i) => ({
    time: (lastTime + (i + 1) * tfSeconds) as UTCTimestamp,
  }));
}

/** Install the autoscaleInfoProvider (saved range or pass-through) and set
 *  autoScale: true so the chart auto-fits via the provider. Does NOT lock
 *  with autoScale: false — that's done by a dedicated visibility-gated
 *  effect in <Chart> so the recompute has a visible paint cycle to settle
 *  against (otherwise a hidden tab freezes at a stale/uninitialized range). */
export function applyPriceRangeOrAutoFit(
  chart: IChartApi,
  series: AnySeries,
  range: { from: number; to: number } | null,
): void {
  if (range) {
    series.applyOptions({
      autoscaleInfoProvider: () => ({
        priceRange: { minValue: range.from, maxValue: range.to },
      }),
    });
  } else {
    series.applyOptions({
      autoscaleInfoProvider: (base: () => unknown) => base() as never,
    });
  }
  chart.priceScale("right").applyOptions({ autoScale: true });
}

// Interpolate a fractional logical position for any time, even if it doesn't
// match a bar exactly (e.g., a 1H-timestamp fib viewed on a 1D chart). Used
// when timeToCoordinate returns null for non-bar times.
export function timeToLogical(time: number, candles: Candle[]): number {
  const n = candles.length;
  if (n === 0) return 0;
  let lo = 0;
  let hi = n;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (candles[mid].time < time) lo = mid + 1;
    else hi = mid;
  }
  if (lo === 0) {
    if (n < 2) return 0;
    const dt = candles[1].time - candles[0].time;
    return -(candles[0].time - time) / dt;
  }
  if (lo === n) {
    if (n < 2) return n - 1;
    const dt = candles[n - 1].time - candles[n - 2].time;
    return n - 1 + (time - candles[n - 1].time) / dt;
  }
  if (candles[lo].time === time) return lo;
  const t1 = candles[lo - 1].time;
  const t2 = candles[lo].time;
  return lo - 1 + (time - t1) / (t2 - t1);
}

// Inverse of timeToLogical: a (fractional) logical bar index → timestamp.
// Extrapolates past the last bar (by the last bar spacing) so a click in the
// empty FUTURE area still yields a time (used when coordinateToTime returns
// null past the appended whitespace).
export function logicalToTime(logical: number, candles: Candle[]): number | null {
  const n = candles.length;
  if (n === 0) return null;
  if (n < 2) return candles[0].time;
  const lastIdx = n - 1;
  if (logical <= 0) return candles[0].time;
  if (logical >= lastIdx) {
    const dt = candles[lastIdx].time - candles[lastIdx - 1].time;
    return candles[lastIdx].time + (logical - lastIdx) * (dt > 0 ? dt : 60);
  }
  const lo = Math.floor(logical);
  return (
    candles[lo].time +
    (candles[lo + 1].time - candles[lo].time) * (logical - lo)
  );
}
